const HEIC_TYPES = new Set(["image/heic", "image/heif", "image/heic-sequence", "image/heif-sequence"]);
const HEIC_EXTENSIONS = /\.(heic|heif)$/i;

function isHeicLike(file) {
  if (!file) return false;
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  return HEIC_TYPES.has(type) || HEIC_EXTENSIONS.test(name);
}

function normalizeFileName(name, mimeType) {
  const fallback = mimeType === "image/png" ? "tattoo-design.png" : "tattoo-design.jpg";
  const trimmed = (name || "").trim();
  if (!trimmed) return fallback;
  if (HEIC_EXTENSIONS.test(trimmed)) {
    return trimmed.replace(HEIC_EXTENSIONS, ".jpg");
  }
  return trimmed;
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not load the selected image."));
    };

    image.src = url;
  });
}

function canvasToJpegFile(image, originalName) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;

    if (!width || !height) {
      reject(new Error("Selected image has invalid dimensions."));
      return;
    }

    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (!context) {
      reject(new Error("Could not process the selected image."));
      return;
    }

    context.drawImage(image, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (!blob || !blob.size) {
          reject(new Error("Could not prepare the selected image for upload."));
          return;
        }

        const baseName = (originalName || "tattoo-design").replace(/\.[^/.]+$/, "");
        resolve(
          new File([blob], `${baseName}.jpg`, {
            type: "image/jpeg",
            lastModified: Date.now(),
          })
        );
      },
      "image/jpeg",
      0.92
    );
  });
}

async function convertImageFileToJpeg(file) {
  const image = await loadImageFromFile(file);
  return canvasToJpegFile(image, file.name);
}

/**
 * Materializes a browser File into upload-safe bytes.
 * iOS Safari can show a preview from a native File reference while later sending
 * an empty multipart body, so we read bytes immediately before upload.
 */
export async function prepareImageForUpload(file) {
  if (!file) return null;

  let preparedFile;

  if (isHeicLike(file)) {
    preparedFile = await convertImageFileToJpeg(file);
  } else {
    try {
      const buffer = await file.arrayBuffer();
      if (!buffer?.byteLength) {
        preparedFile = await convertImageFileToJpeg(file);
      } else {
        const mimeType = file.type || "image/jpeg";
        preparedFile = new File([buffer], normalizeFileName(file.name, mimeType), {
          type: mimeType,
          lastModified: Date.now(),
        });
      }
    } catch {
      preparedFile = await convertImageFileToJpeg(file);
    }
  }

  if (!preparedFile?.size) {
    throw new Error("Selected image could not be processed. Please try another photo.");
  }

  return preparedFile;
}
